import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  downloadCsvFile,
  formatPurchaseOrderCreated,
  listLabelPurchaseOrders,
  previewLabelPurchaseOrder,
  printLabelPurchaseOrder,
  refreshLabelPurchaseOrders,
  voidLabelPurchaseOrder,
  type LabelCsvRow,
  type LabelPurchaseOrderRow,
} from "@/lib/printingApi";

const HEADER_CELL =
  "sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-2 py-2 text-xs font-medium uppercase tracking-wide text-slate-600 backdrop-blur-sm";

function CsvPreviewTable({ rows }: { rows: LabelCsvRow[] }) {
  const columns = useMemo(() => {
    if (rows.length === 0) {
      return [];
    }
    return Object.keys(rows[0]!);
  }, [rows]);

  if (rows.length === 0) {
    return <p className="text-sm text-slate-600">No preview rows.</p>;
  }

  return (
    <div className="max-h-[28rem] overflow-auto rounded-md border border-slate-100">
      <table className="w-full min-w-[48rem] border-collapse text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} className={cn(HEADER_CELL, "text-left")}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-slate-100">
              {columns.map((column) => (
                <td key={column} className="px-2 py-2 align-top text-slate-800">
                  {row[column] == null ? "" : String(row[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function LabelOrdersPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState<LabelCsvRow[]>([]);
  const [previewMeta, setPreviewMeta] = useState<{
    purchaseOrderNumber: string;
    totalRows: number;
    isReprint: boolean;
    purchaseOrderId: number;
  } | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const ordersQuery = useQuery({
    queryKey: ["printing", "purchase-orders"],
    queryFn: listLabelPurchaseOrders,
  });

  const rows = ordersQuery.data ?? [];
  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;

  const refreshMutation = useMutation({
    mutationFn: refreshLabelPurchaseOrders,
    onSuccess: async () => {
      setStatusMessage("Megaventory purchase orders refreshed.");
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ["printing", "purchase-orders"] });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  const previewMutation = useMutation({
    mutationFn: previewLabelPurchaseOrder,
    onSuccess: (result, purchaseOrderId) => {
      const row = rows.find((entry) => entry.id === purchaseOrderId);
      setPreviewRows(result.rows);
      setPreviewMeta({
        purchaseOrderId,
        purchaseOrderNumber: result.core.purchase_order_number ?? row?.number ?? String(purchaseOrderId),
        totalRows: result.core.total_rows ?? result.rows.length,
        isReprint: Boolean(result.core.is_reprint),
      });
      setPreviewOpen(true);
      setErrorMessage(null);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  const printMutation = useMutation({
    mutationFn: printLabelPurchaseOrder,
    onSuccess: async (result) => {
      for (const file of result.files) {
        downloadCsvFile(file.filename, file.content);
      }
      setStatusMessage(
        result.files.length === 1
          ? `Downloaded ${result.files[0]!.filename}.`
          : `Downloaded ${result.files.length} CSV files.`,
      );
      setPreviewOpen(false);
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ["printing", "purchase-orders"] });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  const voidMutation = useMutation({
    mutationFn: voidLabelPurchaseOrder,
    onSuccess: async () => {
      setStatusMessage("Order voided successfully.");
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ["printing", "purchase-orders"] });
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  function selectRow(row: LabelPurchaseOrderRow) {
    setSelectedId(row.id);
  }

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint="Generate printer CSV files for label purchase orders. Preview before committing serials, void when lines change, then reprint.">
        Label order generator
      </PageTitleWithHelp>
      <p className="text-sm text-slate-600">
        Purchase orders with at least one mapped label SKU. Preview CSV layout, then submit to reserve serials and download one file per art.
      </p>

      {statusMessage ? <p className="text-sm text-emerald-700">{statusMessage}</p> : null}
      {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}

      <HubViewportCard chrome="extendedHeader">
        <CardHeader className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <CardTitle>Label purchase orders</CardTitle>
          <Button
            type="button"
            variant="primary"
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            {refreshMutation.isPending ? "Refreshing…" : "Refresh Megaventory POs"}
          </Button>
        </CardHeader>
        <CardBody className="grid min-h-0 flex-1 gap-6 p-0 lg:grid-cols-2">
          <div className="min-h-0 overflow-auto px-5 pb-5">
            {ordersQuery.isLoading ? <p className="text-sm text-slate-600">Loading…</p> : null}
            {ordersQuery.isError ? (
              <p className="text-sm text-red-700">Could not load label purchase orders.</p>
            ) : null}
            {!ordersQuery.isLoading && !ordersQuery.isError ? (
              <table className="w-full min-w-[36rem] border-collapse text-sm">
                <thead>
                  <tr>
                    <th className={cn(HEADER_CELL, "text-left")}>Number</th>
                    <th className={cn(HEADER_CELL, "text-left")}>Status</th>
                    <th className={cn(HEADER_CELL, "text-left")}>Location</th>
                    <th className={cn(HEADER_CELL, "text-left")}>Supplier</th>
                    <th className={cn(HEADER_CELL, "text-left")}>Created</th>
                    <th className={cn(HEADER_CELL, "text-center")}>CSV</th>
                    <th className={cn(HEADER_CELL, "text-right")}>Gen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-2 py-4 text-slate-600">
                        No label purchase orders found.
                      </td>
                    </tr>
                  ) : null}
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        "cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50",
                        selectedId === row.id && "bg-brand-50 hover:bg-brand-50",
                      )}
                      onClick={() => selectRow(row)}
                    >
                      <td className="px-2 py-2 font-medium text-slate-900">{row.number}</td>
                      <td className="px-2 py-2">{row.mv_status ?? "—"}</td>
                      <td className="px-2 py-2">{row.location_name ?? "—"}</td>
                      <td className="px-2 py-2">{row.mv_supplier_name ?? "—"}</td>
                      <td className="px-2 py-2">{formatPurchaseOrderCreated(row.created)}</td>
                      <td className="px-2 py-2 text-center">{row.active_csv ? "Yes" : ""}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.generation ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>

          <div className="flex min-h-0 flex-col border-t border-slate-100 px-5 pb-5 lg:border-l lg:border-t-0">
            <div className="flex flex-wrap items-center gap-2 py-4">
              <Button
                type="button"
                variant="secondary"
                disabled={!selectedRow || previewMutation.isPending}
                onClick={() => selectedRow && previewMutation.mutate(selectedRow.id)}
              >
                Preview CSV
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={!selectedRow || voidMutation.isPending}
                onClick={() => selectedRow && voidMutation.mutate(selectedRow.id)}
              >
                Void CSV
              </Button>
            </div>
            <h3 className="text-sm font-semibold text-slate-900">Purchase order lines</h3>
            {!selectedRow ? (
              <p className="mt-2 text-sm text-slate-600">Select a purchase order to view lines.</p>
            ) : (
              <div className="mt-3 min-h-0 flex-1 overflow-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className={cn(HEADER_CELL, "text-left")}>SKU</th>
                      <th className={cn(HEADER_CELL, "text-right")}>Qty</th>
                      <th className={cn(HEADER_CELL, "text-left")}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRow.items.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100">
                        <td className="px-2 py-2">{item.sku}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{item.quantity}</td>
                        <td className="px-2 py-2 text-slate-600">{item.description ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardBody>
      </HubViewportCard>

      {previewOpen && previewMeta ? (
        <DocumentModal
          title={`CSV Preview (${previewMeta.purchaseOrderNumber}; ${previewMeta.totalRows} labels${previewMeta.isReprint ? "; REPRINT" : ""})`}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewRows([]);
            setPreviewMeta(null);
          }}
          showCloseButton={false}
          shellClassName="max-w-6xl"
        >
          <CsvPreviewTable rows={previewRows} />
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setPreviewOpen(false);
                setPreviewRows([]);
                setPreviewMeta(null);
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={printMutation.isPending}
              onClick={() => printMutation.mutate(previewMeta.purchaseOrderId)}
            >
              {printMutation.isPending
                ? "Generating…"
                : previewMeta.isReprint
                  ? "Reprint CSV"
                  : "Submit and generate CSV"}
            </Button>
          </div>
        </DocumentModal>
      ) : null}
    </div>
  );
}
